import { useCallback, useMemo, useState } from 'react';
import {
  PointerSensor,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { WIDGET_DEFINITIONS, type WidgetType } from '../../templates/widgets/widget-registry';
import { createBlockInstance } from '@/utils/email-blocks';
import { useAppDispatch } from '@/store/hooks';
import { insertBlock, reorderBlocks, setSelectedBlockId } from '@/store/slices/campaign-editor';

const PREVIEW_DROP_ZONE_ID = 'campaign-preview-drop-zone';

interface UseWidgetDragAndDropArgs {
  isDropEnabled: boolean;
}

export function useWidgetDragAndDrop({ isDropEnabled }: UseWidgetDragAndDropArgs) {
  const dispatch = useAppDispatch();
  const [draggedWidgetType, setDraggedWidgetType] = useState<WidgetType | null>(null);
  const [isDragActive, setIsDragActive] = useState(false);

  const dndSensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));
  const { setNodeRef: setPreviewDropRef, isOver: isPreviewDropOver } = useDroppable({
    id: PREVIEW_DROP_ZONE_ID,
    disabled: !isDropEnabled,
  });
  const draggedWidget = useMemo(
    () => WIDGET_DEFINITIONS.find((widget) => widget.type === draggedWidgetType) ?? null,
    [draggedWidgetType]
  );

  const handleWidgetDragStart = useCallback((event: DragStartEvent) => {
    setDraggedWidgetType((event.active.data.current?.widgetType as WidgetType | undefined) ?? null);
    setIsDragActive(true);
  }, []);

  const handleWidgetDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      const widgetType = active.data.current?.widgetType as WidgetType | undefined;

      if (widgetType && over) {
        const newBlock = createBlockInstance(widgetType);
        dispatch(
          insertBlock({ block: newBlock, afterId: over.id === PREVIEW_DROP_ZONE_ID ? undefined : String(over.id) })
        );
        dispatch(setSelectedBlockId(newBlock.id));
      } else if (!widgetType && over && active.id !== over.id) {
        // not a widget-panel tile, so this is an existing block being dragged to reorder
        dispatch(reorderBlocks({ activeId: String(active.id), overId: String(over.id) }));
      }

      setDraggedWidgetType(null);
      setIsDragActive(false);
    },
    [dispatch]
  );

  return {
    dndSensors,
    setPreviewDropRef,
    isPreviewDropOver,
    draggedWidget,
    isDragActive,
    handleWidgetDragStart,
    handleWidgetDragEnd,
  };
}
